import React, { useState, useEffect, useContext, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./review-test-order.css";
import Loading from "../loading/Loading";
import { GlobalContext } from "../../GlobalContext";
import { Modal } from "@mantine/core";
import PatientConsentForm from "../consent-forms/patient-consent-form";
import ProviderConsentForm from "../consent-forms/provider-consent-form";
import { ArrowRight } from "lucide-react";

const ReviewTestOrder = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const testSlug = searchParams.get("test_slug");
  const { userData: globalUserData } = useContext(GlobalContext);

  // State for test data
  const [testData, setTestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  // State for billing details
  const [billingData, setBillingData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    streetAddress: "",
    city: "",
    state: "",
  });
  const [billingErrors, setBillingErrors] = useState({});
  const [billingTouched, setBillingTouched] = useState({
    firstName: false,
    lastName: false,
    email: false,
    streetAddress: false,
    city: false,
    state: false,
  });
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  // State for provider patient search
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const searchRef = useRef(null);

  // State for consent form
  const [consentModalOpened, setConsentModalOpened] = useState(false);
  const [consentSubmitted, setConsentSubmitted] = useState(false);
  const [consentModalMode, setConsentModalMode] = useState("new");
  const [existingConsentData, setExistingConsentData] = useState(null);

  // State for coupon
  const [showDiscountInput, setShowDiscountInput] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [isApplying, setIsApplying] = useState(false);

  // State for order placement
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [notification, setNotification] = useState(null);

  const userRole = globalUserData.role === "patient" ? "individual" : "provider";
  const currentUserId = globalUserData.id;

  const TEST_API_ENDPOINT = "/wp-json/dbsb/v1/medical-test";
  const USER_API_ENDPOINT = "/wp-json/dbsb/v1/user/me";
  const PATIENTS_API_ENDPOINT = "/wp-json/dbsb/v1/user/patients";
  const CONSENT_CHECK_API_ENDPOINT = userRole === "provider" 
    ? "/wp-json/dbsb/v1/consent-forms/check/provider"
    : "/wp-json/dbsb/v1/consent-forms/check/patient";
  const CONSENT_GET_API_ENDPOINT = userRole === "provider"
    ? "/wp-json/dbsb/v1/consent-forms/user/provider"
    : "/wp-json/dbsb/v1/consent-forms/user/patient";
  const CONSENT_API_ENDPOINT = "/wp-json/dbsb/v1/consent-forms/submit";
  const CONSENT_UPDATE_API_ENDPOINT = "/wp-json/dbsb/v1/consent-forms";
  const ORDER_API_ENDPOINT = "/wp-json/dbsb/v1/orders";
  const COUPON_VALIDATE_API_ENDPOINT = "/wp-json/dbsb/v1/coupons/validate";

  // Fetch test details
  useEffect(() => {
    const fetchTestDetails = async () => {
      if (!testSlug) {
        setFetchError("No test selected.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setFetchError(null);

      try {
        if (!window.wpApiSettings?.nonce) {
          throw new Error(
            "Authentication token not found. Please try logging in again."
          );
        }

        const response = await fetch(
          `${TEST_API_ENDPOINT}?test_slug=${testSlug}`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              "X-WP-Nonce": window.wpApiSettings.nonce,
            },
          }
        );

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error("Test not found.");
          } else if (response.status === 401) {
            throw new Error("You must be logged in to view test details.");
          } else if (response.status === 403) {
            throw new Error("You don't have permission to view this test.");
          }
          throw new Error("Failed to fetch test details.");
        }

        const data = await response.json();
        setTestData({
          id: data.id,
          title: data.title,
          slug: data.slug,
          price: data.test_price,
          processingTime: data.processing_time,
          sampleType: data.sample_type,
        });
      } catch (err) {
        console.error("Error fetching test details:", err);
        setFetchError(err.message || "Failed to fetch test details.");
      } finally {
        setLoading(false);
      }
    };

    fetchTestDetails();
  }, [testSlug]);

  // Fetch user data for auto-fill
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await fetch(USER_API_ENDPOINT, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-WP-Nonce": window.wpApiSettings?.nonce || "",
          },
        });
        if (!response.ok) throw new Error("Failed to fetch user details");
        const userData = await response.json();
        
        if (userRole === "individual") {
          setBillingData({
            firstName: userData.first_name || "",
            lastName: userData.last_name || "",
            email: userData.email || "",
            streetAddress: "",
            city: "",
            state: "",
          });
        }
      } catch (error) {
        console.error("Error fetching user details:", error);
      }
    };
    fetchUserData();
  }, [userRole]);

  // Fetch patients for providers
  useEffect(() => {
    const fetchPatients = async () => {
      if (userRole !== "provider") return;
      try {
        const response = await fetch(PATIENTS_API_ENDPOINT, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-WP-Nonce": window.wpApiSettings?.nonce || "",
          },
        });
        if (!response.ok) throw new Error("Failed to fetch patients");
        const data = await response.json();
        setPatients(data.patients || []);
        setFilteredPatients(data.patients || []);
      } catch (error) {
        console.error("Error fetching patients:", error);
      }
    };
    fetchPatients();
  }, [userRole]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Check for existing consent form
  useEffect(() => {
    const checkExistingConsent = async () => {
      try {
        if (!window.wpApiSettings?.nonce) {
          return;
        }

        const response = await fetch(CONSENT_CHECK_API_ENDPOINT, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-WP-Nonce": window.wpApiSettings.nonce,
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.has_consent) {
            const consentResponse = await fetch(CONSENT_GET_API_ENDPOINT, {
              method: "GET",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
                "X-WP-Nonce": window.wpApiSettings.nonce,
              },
            });

            if (consentResponse.ok) {
              const consentData = await consentResponse.json();
              setExistingConsentData(consentData.consent_form);
              setConsentSubmitted(true);
            }
          }
        }
      } catch (error) {
        console.error("Error checking existing consent:", error);
      }
    };

    checkExistingConsent();
  }, [userRole]);

  // Handle patient search
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Reset form state when editing search input
    setSelectedPatient(null);
    setBillingData({
      firstName: "",
      lastName: "",
      email: "",
      streetAddress: billingData.streetAddress, // Keep address fields
      city: billingData.city,
      state: billingData.state,
    });
    setBillingTouched({
      firstName: false,
      lastName: false,
      email: false,
      streetAddress: billingTouched.streetAddress,
      city: billingTouched.city,
      state: billingTouched.state,
    });
    // Clear errors for firstName, lastName, email
    setBillingErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.firstName;
      delete newErrors.lastName;
      delete newErrors.email;
      return newErrors;
    });
    
    if (query) {
      const filtered = patients.filter((patient) =>
        `${patient.first_name} ${patient.last_name}`
          .toLowerCase()
          .includes(query.toLowerCase())
      );
      setFilteredPatients(filtered);
      setShowDropdown(true);
        } else {
      setFilteredPatients(patients);
      setShowDropdown(true);
    }
  };

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setSearchQuery(`${patient.first_name} ${patient.last_name}`);
    setShowDropdown(false);

    setBillingData({
      firstName: patient.first_name,
      lastName: patient.last_name,
      email: patient.email,
      streetAddress: billingData.streetAddress, // Keep address fields
      city: billingData.city,
      state: billingData.state,
    });
    
    // Mark firstName, lastName, email as touched and clear their errors
    setBillingTouched((prev) => ({
      ...prev,
      firstName: true,
      lastName: true,
      email: true,
    }));
    
    // Clear errors for firstName, lastName, email since they're now filled
    setBillingErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.firstName;
      delete newErrors.lastName;
      delete newErrors.email;
      return newErrors;
    });
  };

  // Handle billing form changes
  const handleBillingChange = (e) => {
    const { name, value } = e.target;
    setBillingData((prev) => ({ ...prev, [name]: value }));
    
    // For providers with selected patient, skip validation for auto-filled fields
    const skipAutoFilledFields = userRole === "provider" && selectedPatient && 
      (name === "firstName" || name === "lastName" || name === "email");
    
    if (skipAutoFilledFields) {
      return; // Don't validate auto-filled fields
    }
    
    // Always validate if field has been touched or if we've attempted submit
    if (hasAttemptedSubmit || billingTouched[name]) {
      setBillingTouched((prev) => ({ ...prev, [name]: true }));
      // Validate with the new value immediately - always clear error if value is valid
      const newErrors = { ...billingErrors };
      if (name === "firstName") {
        if (value.trim()) {
          delete newErrors.firstName;
        } else {
          newErrors.firstName = "First name is required";
        }
      } else if (name === "lastName") {
        if (value.trim()) {
          delete newErrors.lastName;
        } else {
          newErrors.lastName = "Last name is required";
        }
      } else if (name === "email") {
        if (value.trim()) {
          if (/\S+@\S+\.\S+/.test(value)) {
            delete newErrors.email;
          } else {
            newErrors.email = "Please enter a valid email address";
          }
        } else {
          newErrors.email = "Email is required";
        }
      } else if (name === "streetAddress") {
        if (value.trim()) {
          delete newErrors.streetAddress;
        } else {
          newErrors.streetAddress = "Street address is required";
        }
      } else if (name === "city") {
        if (value.trim()) {
          delete newErrors.city;
        } else {
          newErrors.city = "City is required";
        }
      } else if (name === "state") {
        if (value.trim()) {
          delete newErrors.state;
        } else {
          newErrors.state = "State is required";
        }
      }
      setBillingErrors(newErrors);
    }
  };

  const handleBillingBlur = (e) => {
    const { name } = e.target;
    
    // For providers with selected patient, skip validation for auto-filled fields
    const skipAutoFilledFields = userRole === "provider" && selectedPatient && 
      (name === "firstName" || name === "lastName" || name === "email");
    
    if (skipAutoFilledFields) {
      return; // Don't validate or mark as touched for auto-filled fields
    }
    
    setBillingTouched((prev) => {
      const updated = { ...prev, [name]: true };
      // Validate immediately with updated touched state
      const newErrors = { ...billingErrors };
      if (name === "firstName") {
        if (billingData.firstName && billingData.firstName.trim()) {
          delete newErrors.firstName;
        } else {
          newErrors.firstName = "First name is required";
        }
      } else if (name === "lastName") {
        if (billingData.lastName && billingData.lastName.trim()) {
          delete newErrors.lastName;
        } else {
          newErrors.lastName = "Last name is required";
        }
      } else if (name === "email") {
        if (billingData.email && billingData.email.trim()) {
          if (/\S+@\S+\.\S+/.test(billingData.email)) {
            delete newErrors.email;
          } else {
            newErrors.email = "Please enter a valid email address";
          }
        } else {
          newErrors.email = "Email is required";
        }
      } else if (name === "streetAddress") {
        if (billingData.streetAddress && billingData.streetAddress.trim()) {
          delete newErrors.streetAddress;
        } else {
          newErrors.streetAddress = "Street address is required";
        }
      } else if (name === "city") {
        if (billingData.city && billingData.city.trim()) {
          delete newErrors.city;
        } else {
          newErrors.city = "City is required";
        }
      } else if (name === "state") {
        if (billingData.state && billingData.state.trim()) {
          delete newErrors.state;
        } else {
          newErrors.state = "State is required";
        }
      }
      setBillingErrors(newErrors);
      return updated;
    });
  };

  const validateBilling = (shouldValidateAll = false) => {
    const newErrors = {};
    const touched = billingTouched;
    
    // For providers with selected patient, skip firstName, lastName, email validation
    const skipAutoFilledFields = userRole === "provider" && selectedPatient;
    
    if (!skipAutoFilledFields) {
      if ((shouldValidateAll || touched.firstName) && !billingData.firstName) {
        newErrors.firstName = "First name is required";
      }
      if ((shouldValidateAll || touched.lastName) && !billingData.lastName) {
        newErrors.lastName = "Last name is required";
      }
      if (shouldValidateAll || touched.email) {
        if (!billingData.email) {
          newErrors.email = "Email is required";
        } else if (!/\S+@\S+\.\S+/.test(billingData.email)) {
          newErrors.email = "Please enter a valid email address";
        }
      }
    }
    
    if ((shouldValidateAll || touched.streetAddress) && !billingData.streetAddress) {
      newErrors.streetAddress = "Street address is required";
    }
    if ((shouldValidateAll || touched.city) && !billingData.city) {
      newErrors.city = "City is required";
    }
    if ((shouldValidateAll || touched.state) && !billingData.state) {
      newErrors.state = "State is required";
    }
    setBillingErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate form when data changes
  useEffect(() => {
    if (hasAttemptedSubmit) {
      const newErrors = {};
      const touched = billingTouched;
      
      // For providers with selected patient, skip firstName, lastName, email validation
      const skipAutoFilledFields = userRole === "provider" && selectedPatient;
      
      if (!skipAutoFilledFields) {
        if (touched.firstName && !billingData.firstName) {
          newErrors.firstName = "First name is required";
        }
        if (touched.lastName && !billingData.lastName) {
          newErrors.lastName = "Last name is required";
        }
        if (touched.email) {
          if (!billingData.email) {
            newErrors.email = "Email is required";
          } else if (!/\S+@\S+\.\S+/.test(billingData.email)) {
            newErrors.email = "Please enter a valid email address";
          }
        }
      }
      
      if (touched.streetAddress && !billingData.streetAddress) {
        newErrors.streetAddress = "Street address is required";
      }
      if (touched.city && !billingData.city) {
        newErrors.city = "City is required";
      }
      if (touched.state && !billingData.state) {
        newErrors.state = "State is required";
      }
      setBillingErrors(newErrors);
    }
  }, [billingData, billingTouched, hasAttemptedSubmit, userRole, selectedPatient]);

  // Handle consent form
  const handleConsentSubmit = async (consentData) => {
    try {
      if (!window.wpApiSettings?.nonce) {
        throw new Error("Authentication token not found. Please try logging in again.");
      }

      const isUpdate = consentData.id;
      const endpoint = isUpdate ? `${CONSENT_UPDATE_API_ENDPOINT}/${consentData.id}` : CONSENT_API_ENDPOINT;
      const method = isUpdate ? "PUT" : "POST";

      const response = await fetch(endpoint, {
        method: method,
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-WP-Nonce": window.wpApiSettings.nonce,
        },
        body: JSON.stringify(consentData),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || data.message || "Failed to submit consent form.");
      }

      if (!isUpdate) {
        try {
          const created = await response.json().catch(() => null);
          if (created) {
            setExistingConsentData(created.consent_form || created);
          }
        } catch (parseError) {
          console.warn("Could not parse created consent response:", parseError);
        }
      }

      if (isUpdate) {
        try {
          const refreshResponse = await fetch(CONSENT_GET_API_ENDPOINT, {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              "X-WP-Nonce": window.wpApiSettings.nonce,
            },
          });

          if (refreshResponse.ok) {
            const refreshData = await refreshResponse.json();
            setExistingConsentData(refreshData.consent_form);
          }
        } catch (refreshError) {
          console.error("Error refreshing consent data:", refreshError);
        }
      }

      setConsentSubmitted(true);
      setConsentModalOpened(false);
      setConsentModalMode("new");
      } catch (error) {
      console.error("Error submitting consent form:", error);
      showNotification(error.message || "Failed to submit consent form. Please try again.");
    }
  };

  const handleEditConsent = () => {
    setConsentModalMode("edit");
    setConsentModalOpened(true);
  };

  // Handle coupon
  const handleDiscountCodeChange = (e) => {
    const value = e.target.value;
    setDiscountCode(value);

    if (appliedCoupon) {
      const normalizedValue = value.trim().toUpperCase();
      if (!normalizedValue || normalizedValue !== appliedCoupon.coupon_code) {
        setAppliedCoupon(null);
      }
    }
  };

  const handleApplyCoupon = async () => {
    if (!discountCode.trim() || !testData) return;
    setIsApplying(true);
    try {
      if (!window.wpApiSettings?.nonce) {
        throw new Error(
          "Authentication token not found. Please try logging in again."
        );
      }
      const response = await fetch(COUPON_VALIDATE_API_ENDPOINT, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-WP-Nonce": window.wpApiSettings.nonce,
          },
          credentials: "same-origin",
          body: JSON.stringify({
            coupon_code: discountCode.trim().toUpperCase(),
          amount: testData.price,
          }),
      });
      const data = await response.json();
      if (!response.ok) {
        if (response.status === 400 && data.code === "invalid_coupon") {
          showNotification("No coupon found");
          } else {
          showNotification(data.message || "Failed to apply coupon");
        }
        return;
      }
      setAppliedCoupon({
        coupon_code: data.coupon_code,
        discount_amount: data.discount_amount ?? 0,
        final_amount: data.final_amount ?? 0,
        original_amount: testData.price,
      });
    } catch (error) {
      console.error("Error applying coupon:", error);
      showNotification(error.message || "Failed to apply coupon");
    } finally {
      setIsApplying(false);
    }
  };

  // Check if form is invalid
  const isFormInvalid = () => {
    // Check if consent form is submitted
    if (!consentSubmitted) return true;
    
    // For providers with selected patient, firstName, lastName, email are auto-filled and should not be checked
    const skipAutoFilledFields = userRole === "provider" && selectedPatient;
    
    // Check if there are any billing errors (excluding auto-filled fields for providers)
    const relevantErrors = { ...billingErrors };
    if (skipAutoFilledFields) {
      delete relevantErrors.firstName;
      delete relevantErrors.lastName;
      delete relevantErrors.email;
    }
    const hasErrors = Object.keys(relevantErrors).length > 0 && 
      Object.values(relevantErrors).some((error) => error !== "" && error !== undefined);
    
    // Check if required fields are filled
    const requiredFields = {
      streetAddress: billingData.streetAddress,
      city: billingData.city,
      state: billingData.state,
    };
    
    // Only check firstName, lastName, email if not auto-filled
    if (!skipAutoFilledFields) {
      requiredFields.firstName = billingData.firstName;
      requiredFields.lastName = billingData.lastName;
      requiredFields.email = billingData.email;
    }
    
    const hasEmptyFields = Object.values(requiredFields).some((value) => !value || value.trim() === "");
    
    return hasErrors || hasEmptyFields;
  };

  // Handle place order
  const handlePlaceOrder = async () => {
    setHasAttemptedSubmit(true);
    
    // Mark all fields as touched
    setBillingTouched({
      firstName: true,
      lastName: true,
      email: true,
      streetAddress: true,
      city: true,
      state: true,
    });

    if (!validateBilling(true)) {
      showNotification("Please fill in all required billing details");
      return;
    }

    if (!consentSubmitted) {
      showNotification("You must complete the consent form");
      return;
    }

    setIsPlacingOrder(true);

    try {
      if (!window.wpApiSettings?.nonce) {
        throw new Error(
          "Authentication token not found. Please try logging in again."
        );
      }

      // Step 1: Create order
      const orderData = {
        order_type: userRole,
        test_slug: testSlug,
        first_name: billingData.firstName,
        last_name: billingData.lastName,
        email: billingData.email,
        street_address: billingData.streetAddress,
        city: billingData.city,
        state: billingData.state,
        patient_user_id: userRole === "individual" ? currentUserId : null,
        health_provider_user_id: userRole === "provider" ? currentUserId : null,
        patient_post_id: userRole === "provider" && selectedPatient ? selectedPatient.id : null,
        coupon_code: appliedCoupon ? appliedCoupon.coupon_code : null,
      };

      const orderResponse = await fetch(ORDER_API_ENDPOINT, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-WP-Nonce": window.wpApiSettings.nonce,
        },
        body: JSON.stringify(orderData),
      });

      const orderResult = await orderResponse.json();

      if (!orderResponse.ok) {
        throw new Error(orderResult.message || "Failed to create order.");
      }

      const orderId = orderResult.order_id;

      // Step 2: Apply coupon if exists (order already has coupon applied, but we need to generate payment link)
      // The coupon is already applied during order creation, so we just need to generate payment link

      // Step 3: Generate payment link
      const paymentResponse = await fetch(
        `/wp-json/dbsb/v1/orders/${encodeURIComponent(orderId)}/generate-payment-link`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-WP-Nonce": window.wpApiSettings.nonce,
          },
          credentials: "same-origin",
          body: JSON.stringify({
            amount: appliedCoupon ? appliedCoupon.final_amount * 100 : testData.price * 100,
          }),
        }
      );

      const paymentResult = await paymentResponse.json();
      if (!paymentResponse.ok) {
        throw new Error(paymentResult.message || "Failed to generate payment link");
      }

      if (paymentResult.payment_link) {
        window.location.href = paymentResult.payment_link;
      } else {
        throw new Error("Payment link not available. Please contact support.");
      }
    } catch (error) {
      console.error("Error placing order:", error);
      showNotification(error.message || "Failed to place order. Please try again.");
      setIsPlacingOrder(false);
    }
  };

  const showNotification = (message, type = "error") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const formatPrice = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return "₦0.00";
    }
    let numAmount;
    if (typeof amount === "string") {
      const cleanAmount = amount.replace(/[₦,]/g, "").replace(/\.00$/, "");
      numAmount = parseFloat(cleanAmount);
    } else {
      numAmount = parseFloat(amount);
    }
    if (isNaN(numAmount)) {
      return "₦0.00";
    }
    return `₦${numAmount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const handleTalkToSpecialist = () => {
    navigate("/talk-to-specialist");
  };

  const toggleDiscountInput = () => {
    setShowDiscountInput(!showDiscountInput);
  };

  const isApplyDisabled = !discountCode.trim() || isApplying;

  // Render loading or error states
  if (loading) {
    return (
      <div className="review-test-order-container">
        <Loading />
      </div>
    );
  }

  if (fetchError || !testData) {
    return (
      <div className="review-test-order-container">
        <div className="error-message">
          {fetchError || "Invalid test data. Please try again or contact support."}
        </div>
      </div>
    );
  }

  const originalPrice = parseFloat(testData.price);
  const totalAmount = appliedCoupon ? appliedCoupon.final_amount : originalPrice;
  const testPrice = formatPrice(originalPrice);
  const totalPrice = formatPrice(totalAmount);

  const formVariants = {
    hidden: { opacity: 0, x: -100 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut", staggerChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: [0.6, -0.05, 0.01, 0.99] },
    },
  };

  const buttonVariants = {
    hover: {
      scale: 1.02,
      backgroundColor: "#d1d9e6",
      transition: { duration: 0.2 },
    },
    tap: { scale: 0.98 },
  };

  const slideVariants = {
    hidden: { opacity: 0, height: 0, marginBottom: 0 },
    visible: {
      opacity: 1,
      height: "auto",
      marginBottom: "1rem",
      transition: { duration: 0.3, ease: "easeOut" },
    },
  };

  return (
    <div className="review-test-order-container">
      <motion.div
        className="review-test-order-content"
        variants={formVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.h2 variants={itemVariants}>Review & pay for order</motion.h2>
        <motion.div className="review-test-order-card" variants={itemVariants}>
          <div className="review-test-order-wrapper">
            {/* Left Side: Test Details and Total */}
            <div className="left-section">
              <div className="test-details-card">
                <p className="test-name">{testData.title}</p>
                <p className="test-price">{testPrice}</p>
              </div>

              {/* Discount Code Section */}
              <motion.div className="discount-section" variants={itemVariants}>
                <button
                  className="discount-toggle-button"
                  onClick={toggleDiscountInput}
                  type="button"
                >
                  <span>Have discount code?</span>
                  <motion.svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    className="discount-arrow"
                    animate={{ rotate: showDiscountInput ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <path
                      d="M4 6l4 4 4-4"
                      stroke="currentColor"
                      strokeWidth="2"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </motion.svg>
                </button>

                <AnimatePresence>
                  {showDiscountInput && (
                    <motion.div
                      className="discount-input-container"
                      variants={slideVariants}
                      initial="hidden"
                      animate="visible"
                      exit="hidden"
                    >
                      <div className="discount-input-wrapper">
                        <input
                          type="text"
                          placeholder="Enter discount code"
                          value={discountCode}
                          onChange={handleDiscountCodeChange}
                          className="discount-input"
                          maxLength={20}
                        />
                        <button
                          type="button"
                          onClick={handleApplyCoupon}
                          disabled={isApplyDisabled}
                          className={`apply-button ${
                            isApplyDisabled ? "disabled" : ""
                          }`}
                        >
                          {isApplying ? "Applying..." : "Apply"}
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              <ul></ul>

              {/* Total Card */}
              <div className="total-card">
                {appliedCoupon ? (
                  <div className="total-breakdown">
                    <div className="total-row">
                      <p className="total-label">Subtotal</p>
                      <p className="total-price">{formatPrice(originalPrice)}</p>
                    </div>
                    <div className="total-row">
                      <p className="total-label">Discount</p>
                      <p className="total-price">-{formatPrice(appliedCoupon.discount_amount)}</p>
                    </div>
                    <div className="total-divider"></div>
                    <div className="total-row final-total">
                      <p className="total-label">Total</p>
                      <p className="total-price">{formatPrice(totalAmount)}</p>
                    </div>
                  </div>
                ) : (
                  <div className="total-single">
                    <p className="total-label">Total</p>
                    <p className="total-price">{totalPrice}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Right Side: Terms, Billing Form, Consent, and Buttons */}
            <div className="right-section">
              <p className="terms-text">
                After payment, a representative will contact you to schedule your sample collection. You can choose between at-home collection (our nurse or phlebotomist visits you) or site collection (you visit one of our partner locations).
                <br />
                <br />
                By continuing, you agree to Syndicate Bio's Terms of Service,
                and acknowledge our Privacy Policy.
              </p>

              {/* Billing Details Form */}
              <motion.div 
                className="billing-details-form"
                variants={itemVariants}
                style={{ marginTop: "48px" }}
              >
                <h3 className="billing-title">Billing details</h3>

                {/* Provider Patient Search */}
                {userRole === "provider" && (
                  <>
                    <div className="form-group" style={{ marginTop: "24px" }}>
                      <label>Search Patient</label>
                      <div className="search-container" ref={searchRef}>
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={handleSearchChange}
                          onFocus={() => setShowDropdown(true)}
                          placeholder="Search for a patient..."
                          className="search-input"
                        />
                        {showDropdown && (
                          <div className="dropdown">
                            {filteredPatients.length > 0 ? (
                              filteredPatients.map((patient) => (
                                <div
                                  key={patient.id}
                                  className="dropdown-item"
                                  onClick={() => handlePatientSelect(patient)}
                                >
                                  {patient.first_name} {patient.last_name}
                                </div>
                              ))
                            ) : (
                              <div className="dropdown-item no-patient-dropdown">
                                No patients found
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {!selectedPatient && (
                      <div className="divider" style={{ marginTop: "24px", marginBottom: "24px" }}>
                        <span>OR</span>
                      </div>
                    )}
                  </>
                )}

                {/* Billing Fields */}
                <div className="form-group name-row" style={{ marginTop: userRole === "provider" && !selectedPatient ? "0" : "24px" }}>
                  <div className="name-input">
                    <label>First Name</label>
                    <input
                      name="firstName"
                      value={billingData.firstName}
                      onChange={handleBillingChange}
                      onBlur={handleBillingBlur}
                      placeholder="Enter first name"
                      disabled={userRole === "individual" || (userRole === "provider" && !!selectedPatient)}
                    />
                    {billingErrors.firstName && (
                      <span className="error">{billingErrors.firstName}</span>
                    )}
                  </div>
                  <div className="name-input">
                    <label>Last Name</label>
                    <input
                      name="lastName"
                      value={billingData.lastName}
                      onChange={handleBillingChange}
                      onBlur={handleBillingBlur}
                      placeholder="Enter last name"
                      disabled={userRole === "individual" || (userRole === "provider" && !!selectedPatient)}
                    />
                    {billingErrors.lastName && (
                      <span className="error">{billingErrors.lastName}</span>
                    )}
                  </div>
                </div>

                <div className="form-group" style={{ marginTop: "24px" }}>
                  <label>Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={billingData.email}
                    onChange={handleBillingChange}
                    onBlur={handleBillingBlur}
                    placeholder="Enter email address"
                    disabled={userRole === "individual" || (userRole === "provider" && !!selectedPatient)}
                  />
                  {billingErrors.email && (
                    <span className="error">{billingErrors.email}</span>
                  )}
                </div>

                <div className="form-group" style={{ marginTop: "24px" }}>
                  <label>Street Address</label>
                  <textarea
                    name="streetAddress"
                    value={billingData.streetAddress}
                    onChange={handleBillingChange}
                    onBlur={handleBillingBlur}
                    placeholder="Enter street address"
                    rows="4"
                  />
                  {billingErrors.streetAddress && (
                    <span className="error">{billingErrors.streetAddress}</span>
                  )}
                </div>

                <div className="form-group" style={{ marginTop: "24px" }}>
                  <label>Town/City</label>
                  <input
                    name="city"
                    value={billingData.city}
                    onChange={handleBillingChange}
                    onBlur={handleBillingBlur}
                    placeholder="Enter town/city"
                  />
                  {billingErrors.city && (
                    <span className="error">{billingErrors.city}</span>
                  )}
                </div>

                <div className="form-group" style={{ marginTop: "24px" }}>
                  <label>State</label>
                  <select
                    name="state"
                    value={billingData.state}
                    onChange={handleBillingChange}
                    onBlur={handleBillingBlur}
                  >
                    <option value="">Select from dropdown</option>
                    <option value="Abuja (FCT)">Abuja (FCT)</option>
                    <option value="Abia">Abia</option>
                    <option value="Adamawa">Adamawa</option>
                    <option value="Akwa Ibom">Akwa Ibom</option>
                    <option value="Anambra">Anambra</option>
                    <option value="Bauchi">Bauchi</option>
                    <option value="Bayelsa">Bayelsa</option>
                    <option value="Benue">Benue</option>
                    <option value="Borno">Borno</option>
                    <option value="Cross River">Cross River</option>
                    <option value="Delta">Delta</option>
                    <option value="Ebonyi">Ebonyi</option>
                    <option value="Edo">Edo</option>
                    <option value="Ekiti">Ekiti</option>
                    <option value="Enugu">Enugu</option>
                    <option value="Gombe">Gombe</option>
                    <option value="Imo">Imo</option>
                    <option value="Jigawa">Jigawa</option>
                    <option value="Kaduna">Kaduna</option>
                    <option value="Kano">Kano</option>
                    <option value="Katsina">Katsina</option>
                    <option value="Kebbi">Kebbi</option>
                    <option value="Kogi">Kogi</option>
                    <option value="Kwara">Kwara</option>
                    <option value="Lagos">Lagos</option>
                    <option value="Nasarawa">Nasarawa</option>
                    <option value="Niger">Niger</option>
                    <option value="Ogun">Ogun</option>
                    <option value="Ondo">Ondo</option>
                    <option value="Osun">Osun</option>
                    <option value="Oyo">Oyo</option>
                    <option value="Plateau">Plateau</option>
                    <option value="Rivers">Rivers</option>
                    <option value="Sokoto">Sokoto</option>
                    <option value="Taraba">Taraba</option>
                    <option value="Yobe">Yobe</option>
                    <option value="Zamfara">Zamfara</option>
                  </select>
                  {billingErrors.state && (
                    <span className="error">{billingErrors.state}</span>
                  )}
                </div>
              </motion.div>

              {/* Consent Form Section */}
              <motion.div
                className="consent-section"
                variants={itemVariants}
                style={{ marginTop: "48px" }}
              >
                {!consentSubmitted ? (
                  <div className="consent-button-container" style={{ marginBottom: "0" }}>
                    <button
                      type="button"
                      onClick={() => {
                        setConsentModalMode("new");
                        setConsentModalOpened(true);
                      }}
                      className="fill-consent-button"
                    >
                      <span>Fill the consent form</span>
                      <ArrowRight size={20} />
                    </button>
                  </div>
                ) : (
                  <div className="consent-acknowledgment" style={{ marginBottom: "0" }}>
                    <p>
                      By continuing, you agree and acknowledge to Syndicate Bio's{" "}
                      <span
                        onClick={handleEditConsent}
                        className="consent-link"
                      >
                        Consent Form.
                      </span>
                    </p>
                  </div>
                )}
              </motion.div>

              {/* Button Group */}
              <div className="button-group" style={{ marginTop: "30px" }}>
                <motion.button
                  onClick={handlePlaceOrder}
                  className={`pay-now-button ${isPlacingOrder || isFormInvalid() ? "disabled" : ""}`}
                  variants={buttonVariants}
                  whileHover={isPlacingOrder || isFormInvalid() ? {} : "hover"}
                  whileTap={isPlacingOrder || isFormInvalid() ? {} : "tap"}
                  disabled={isPlacingOrder || isFormInvalid()}
                >
                  {isPlacingOrder ? "Placing Order" : "Place order"}
                </motion.button>
                <motion.button
                  onClick={handleTalkToSpecialist}
                  className="go-back-button"
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                >
                  Talk to a specialist
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Consent Form Modal */}
      <Modal
        opened={consentModalOpened}
        onClose={() => {
          setConsentModalOpened(false);
          setConsentModalMode("new");
        }}
        withCloseButton={false}
        centered
        size="lg"
        className="consent-modal"
        overlayProps={{
          opacity: 0.55,
          blur: 3,
        }}
      >
        {userRole === "provider" ? (
          <ProviderConsentForm
            onClose={() => {
              setConsentModalOpened(false);
              setConsentModalMode("new");
            }}
            onSubmit={handleConsentSubmit}
            isEditMode={consentModalMode === "edit"}
            existingData={consentModalMode === "edit" ? existingConsentData : null}
          />
        ) : (
          <PatientConsentForm
            onClose={() => {
              setConsentModalOpened(false);
              setConsentModalMode("new");
            }}
            onSubmit={handleConsentSubmit}
            isEditMode={consentModalMode === "edit"}
            existingData={consentModalMode === "edit" ? existingConsentData : null}
          />
        )}
      </Modal>

      {/* Notification Popup */}
      <AnimatePresence>
        {notification && (
          <motion.div
            className="notification-popup"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            transition={{ duration: 0.3 }}
          >
            {notification.message}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ReviewTestOrder;
